import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { buyerFavoritesService } from '../../../services/buyerService';

const FavoritesTab = ({ language }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const content = {
    en: {
      title: 'Saved Favorites',
      noFavorites: 'No favorites saved yet',
      loading: 'Loading favorites...',
      remove: 'Remove',
      viewDetails: 'View Details',
      price: 'Price',
      available: 'Available',
      country: 'Origin'
    },
    zh: {
      title: '已保存的收藏',
      noFavorites: '尚未保存收藏',
      loading: '加载收藏中...',
      remove: '移除',
      viewDetails: '查看详情',
      price: '价格',
      available: '可用',
      country: '原产地'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    const controller = new AbortController();
    loadFavorites(controller.signal);
    return () => controller.abort();
  }, []);

  const loadFavorites = async (signal) => {
    try {
      setLoading(true);
      setError('');
      const data = await buyerFavoritesService?.getAll();
      if (signal?.aborted) return;
      setFavorites(data);
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.message !== 'signal is aborted without reason') {
        setError(err?.message || 'Failed to load favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (beanId) => {
    try {
      await buyerFavoritesService?.remove(beanId);
      setFavorites(prev => prev?.filter(fav => fav?.bean?.id !== beanId));
    } catch (err) {
      console.error('Failed to remove favorite:', err);
    }
  };

  const handleViewDetails = (beanId) => {
    navigate(`/bean-detail?id=${beanId}`);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">{t?.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (favorites?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Heart" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground text-lg">{t?.noFavorites}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {favorites?.map((fav) => {
          const bean = fav?.bean;
          const primaryImage = bean?.images?.find(img => img?.isPrimary) || bean?.images?.[0];
          const imageUrl = primaryImage?.imageUrl || 'https://images.unsplash.com/photo-1447933601403-0c6688de566e';

          return (
            <div key={fav?.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-smooth">
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={imageUrl}
                  alt={language === 'en' ? bean?.nameEn : bean?.nameZh}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleRemove(bean?.id)}
                  className="absolute top-2 right-2 p-2 bg-card/90 backdrop-blur-sm rounded-full hover:bg-destructive hover:text-destructive-foreground transition-smooth"
                  aria-label={t?.remove}
                >
                  <Icon name="Heart" size={20} className="fill-current" />
                </button>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                  {language === 'en' ? bean?.nameEn : bean?.nameZh}
                </h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t?.country}:</span>
                    <span className="text-foreground font-medium">
                      {language === 'en' ? bean?.country?.nameEn : bean?.country?.nameZh}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t?.price}:</span>
                    <span className="text-primary font-semibold">${bean?.priceUsdPerKg}/kg</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t?.available}:</span>
                    <span className="text-foreground">{bean?.availableQtyKg} kg</span>
                  </div>
                </div>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => handleViewDetails(bean?.id)}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  {t?.viewDetails}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FavoritesTab;